import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { EventConflict } from '../../shared/api/types';
import { Alert } from '../../shared/ui/components';
import { formatDayHeading, formatTime } from '../calendar/dates';

const KIND: Record<EventConflict['kind'], string> = { event: 'Evento', worship: 'Culto', calendar: 'Calendario', other: 'Agenda' };

/** Lista de lo que se cruza en horario, con el mismo lugar resaltado. */
export function ConflictList({ items }: { items: EventConflict[] }) {
  const place = items.some((c) => c.samePlace);
  return (
    <Alert tone="warning">
      <strong>{place ? '⚠ Choque: ya hay algo en el mismo lugar y horario' : '⚠ A esa hora también hay:'}</strong>
      <ul className="conflicts">
        {items.map((c, i) => (
          <li key={i}>
            <span className="badge">{KIND[c.kind]}</span> {c.link ? <Link to={c.link}>{c.title}</Link> : c.title}
            {c.pending ? <span className="muted"> (por aprobar)</span> : null}
            <span className="muted small">
              {' '}
              · {formatDayHeading(new Date(c.startsAt))} {formatTime(c.startsAt)}–{formatTime(c.endsAt)}
              {c.place ? ` · ${c.place}` : ''}
            </span>
            {c.samePlace ? <strong> · mismo lugar</strong> : null}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/** Mientras se llena el formulario: avisa si el horario se cruza con otro evento o culto. */
export function ConflictsNotice({ churchId, startsAt, endsAt, place, exclude }: { churchId: string; startsAt: string; endsAt: string; place: string; exclude?: string }) {
  const [items, setItems] = useState<EventConflict[]>([]);
  useEffect(() => {
    const s = new Date(startsAt);
    const e = new Date(endsAt);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e <= s) {
      setItems([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ startsAt: s.toISOString(), endsAt: e.toISOString(), ...(place.trim() ? { place: place.trim() } : {}), ...(exclude ? { exclude } : {}) });
      api
        .get<{ items: EventConflict[] }>(`/calendar/churches/${churchId}/church-events/conflicts?${qs}`, ctrl.signal)
        .then((r) => setItems(r.items))
        .catch(() => undefined);
    }, 400);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [churchId, startsAt, endsAt, place, exclude]);
  return items.length > 0 ? <ConflictList items={items} /> : null;
}
